/**
 * aiService.js — AI content & image generation
 *
 * Two modes, controlled by the user's key-source preference in localStorage:
 *
 *  'env'    → Route through the Laravel backend proxy.
 *             The backend uses ANTHROPIC_API_KEY / OPENAI_API_KEY from its
 *             own .env file. The key is never sent to the browser.
 *             Backend endpoints required:
 *               POST /api/ai/generate  — text generation (Anthropic)
 *               POST /api/ai/image     — image generation (OpenAI)
 *
 *  'custom' → Call the vendor APIs directly from the browser using the
 *             key the user pasted into Settings → API Keys.
 */

import client from '../api/client';

// ── helpers ───────────────────────────────────────────────────────────────
function anthSource() { return localStorage.getItem('tayst_anth_source') || 'custom'; }
function oaiSource()  { return localStorage.getItem('tayst_oai_source')  || 'custom'; }

// ─────────────────────────────────────────────────────────────────────────
export const aiService = {

  /**
   * Generate text content via Anthropic Claude.
   *
   * @param {object} opts
   * @param {object} opts.voiceProfile  - Brand voice settings from context
   * @param {object} opts.idea          - The idea to write content for
   * @param {string[]} opts.channels    - Which channels to generate for
   * @param {string} opts.anthropicKey  - Anthropic API key (used in 'custom' mode only)
   * @returns {Promise<string>}         - Raw text response
   */
  async generateContent({ voiceProfile, idea, channels, anthropicKey }) {

    const channelInstructions = channels.map(ch => {
      const map = {
        linkedin:  'LinkedIn post (1300 chars max, professional, 3-5 hashtags)',
        facebook:  'Facebook post (500 chars max, engaging, 1-2 hashtags)',
        instagram: 'Instagram caption (2200 chars max, visual, 5-10 hashtags)',
        blog:      'Blog post intro paragraph (200 words, SEO-friendly)',
        partner:   'Partner brief with [FINISHED POST] and [TALKING POINTS] sections',
      };
      return map[ch] || ch;
    }).join('\n');

    const systemPrompt = [
      `You are a content writer for Tayst Coffee.`,
      voiceProfile?.description ? `Brand voice: ${voiceProfile.description}.` : '',
      voiceProfile?.audience    ? `Audience: ${voiceProfile.audience}.` : '',
      voiceProfile?.tones?.length ? `Tones: ${voiceProfile.tones.join(', ')}.` : '',
      voiceProfile?.banned?.length ? `Never use: ${voiceProfile.banned.join(', ')}.` : '',
    ].filter(Boolean).join(' ');

    const userPrompt = `Write content for this idea:
Title: ${idea.title}
Category: ${idea.category}
Notes: ${idea.notes || ''}
Customer: ${idea.customer || ''}
Urgency: ${idea.urgency}

Generate for these channels (one section per channel, labelled [CHANNEL_NAME]):
${channelInstructions}`;

    // ── Server key: proxy through backend ─────────────────────────────────
    if (anthSource() === 'env') {
      const { data } = await client.post('/ai/generate', {
        system:     systemPrompt,
        messages:   [userPrompt],
        model:      'claude-opus-4-6',
        max_tokens: 2048,
      });
      return data.results?.[0]?.answer ?? '';
    }

    // ── Custom key: call Anthropic directly from the browser ──────────────
    if (!anthropicKey) {
      throw new Error('Anthropic key not set. Go to Settings → API Keys.');
    }

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method:  'POST',
      headers: {
        'x-api-key':         anthropicKey,
        'anthropic-version': '2023-06-01',
        'content-type':      'application/json',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model:      'claude-opus-4-6',
        max_tokens: 2048,
        system:     systemPrompt,
        messages:   [{ role: 'user', content: userPrompt }],
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || `Anthropic API error ${response.status}`);
    }

    const data = await response.json();
    return data.content?.[0]?.text || '';
  },

  /**
   * Low-level text completion — used by components that build their own prompts
   * (FeedScan, ConsumerIdeas, ConsumerCreate).
   *
   * Respects the same 'env' / 'custom' source preference as generateContent.
   *
   * @param {object} opts
   * @param {string} opts.message      - User message / prompt
   * @param {string} [opts.system]     - Optional system prompt
   * @param {number} [opts.maxTokens]  - Defaults to 1024
   * @param {string} opts.anthropicKey - Used only in 'custom' mode
   * @returns {Promise<string>}        - Raw text response
   */
  async rawComplete({ message, system = '', maxTokens = 1024, anthropicKey }) {

    // ── Server key: proxy through backend ─────────────────────────────────
    if (anthSource() === 'env') {
      const { data } = await client.post('/ai/generate', {
        ...(system ? { system } : {}),
        messages:   [message],
        model:      'claude-opus-4-6',
        max_tokens: maxTokens,
      });
      return data.results?.[0]?.answer ?? '';
    }

    // ── Custom key: call Anthropic directly from the browser ──────────────
    if (!anthropicKey) {
      throw new Error('Anthropic key not set. Go to Settings → API Keys.');
    }

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method:  'POST',
      headers: {
        'x-api-key':         anthropicKey,
        'anthropic-version': '2023-06-01',
        'content-type':      'application/json',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model:      'claude-opus-4-6',
        max_tokens: maxTokens,
        ...(system ? { system } : {}),
        messages:   [{ role: 'user', content: message }],
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || `Anthropic API error ${response.status}`);
    }

    const data = await response.json();
    return data.content?.[0]?.text || '';
  },

  /**
   * Generate an image via OpenAI DALL-E 3.
   *
   * @param {object} opts
   * @param {string} opts.prompt    - Image description
   * @param {string} opts.openaiKey - OpenAI API key (used in 'custom' mode only)
   * @returns {Promise<string|null>} - Image URL
   */
  async generateImage({ prompt, openaiKey }) {

    // ── Server key: proxy through backend ─────────────────────────────────
    if (oaiSource() === 'env') {
      const { data } = await client.post('/ai/image', {
        prompt,
        model:   'dall-e-3',
        n:       1,
        size:    '1024x1024',
        quality: 'standard',
      });
      return data.url ?? data.data?.[0]?.url ?? null;
    }

    // ── Custom key: call OpenAI directly from the browser ─────────────────
    if (!openaiKey) {
      throw new Error('OpenAI key not set. Go to Settings → API Keys.');
    }

    const response = await fetch('https://api.openai.com/v1/images/generations', {
      method:  'POST',
      headers: {
        'Authorization': `Bearer ${openaiKey}`,
        'Content-Type':  'application/json',
      },
      body: JSON.stringify({
        model:   'dall-e-3',
        prompt,
        n:       1,
        size:    '1024x1024',
        quality: 'standard',
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || `OpenAI error ${response.status}`);
    }

    const data = await response.json();
    return data.data?.[0]?.url || null;
  },
};
